// SupportApi.tsx
import axios from 'axios';
import apiConfig from '../apiConfig.ts';

const API_BASE_URL = apiConfig.apiBaseURL;

// Support ticket interfaces
export interface Ticket {
  id: string;
  ticketId: string;
  userName?: string;
  userEmail?: string;
  subject?: string;
  supportTicketRaisedDate?: string;
  status: string;
  attachments?: string[];
  description?: string;
  updatedAt?: string;
  // Legacy properties for backward compatibility
  title?: string;
  createdAt?: string;
  user?: {
    id?: string;
    userId?: string;
    name?: string;
    email?: string;
    phoneNumber?: string;
  };
  conversation?: Array<{
    _id?: string;
    sender: string;
    message: string;
    timestamp: string;
    attachments?: string[];
    profileUrl?: string;
    adminProfileUrl?: string;
  }>;
}

// API Response interfaces
export interface TicketResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: Ticket[];
  pagination?: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
  };
}

export interface TicketDetailResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: Ticket;
}

export interface ReplyResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: any;
}

export interface TicketCountsResponse {
  status: 'Success' | 'Failed';
  message: string;
  ticketCounts?: {
    total: number;
    open: number;
    inProgress: number;
    completed: number;
  };
}

// Parameters for API calls
export interface GetTicketsParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export interface ReplyParams {
  message: string;
  files?: FileList | null;
}

export interface UpdateStatusParams {
  status: string;
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

// Helper function to get auth headers for file uploads (no Content-Type)
const getFileUploadHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    ...(token && { Authorization: `Bearer ${token}` })
    // Don't set Content-Type for FormData - axios will set it automatically with boundary
  };
};

export const supportApi = {
  // Get all tickets with pagination, search, and filtering
  getAllTickets: async (params: GetTicketsParams = {}): Promise<TicketResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.status) queryParams.append('status', params.status);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const response = await axios.get(
        `${API_BASE_URL}/support/admin/tickets?${queryParams.toString()}`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching tickets:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch tickets',
        data: [],
        pagination: {
          totalCount: 0,
          totalPages: 0,
          currentPage: 1,
        },
      };
    }
  },

  // Get ticket counts/stats
  getTicketCounts: async (): Promise<TicketCountsResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/support/admin/count`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching ticket counts:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch ticket counts',
        ticketCounts: {
          total: 0,
          open: 0,
          inProgress: 0,
          completed: 0
        }
      };
    }
  },

  // Get single ticket details with conversation
  getTicketDetails: async (ticketId: string): Promise<TicketDetailResponse> => {
    try {
      // jhani
      const response = await axios.get(
        `${API_BASE_URL}/support/${ticketId}`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching ticket details:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch ticket details'
      };
    }
  },

  // Reply to a ticket
  replyToTicket: async (ticketId: string, params: ReplyParams): Promise<ReplyResponse> => {
    try {
      const formData = new FormData();
      formData.append('message', params.message);

      if (params.files && params.files.length > 0) {
        Array.from(params.files).forEach((file) => {
          formData.append('attachments', file);
        });
      }

      const response = await axios.post(
        `${API_BASE_URL}/support/admin/ticket/${ticketId}/reply`,
        formData,
        {
          headers: getFileUploadHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error sending reply:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to send reply'
      };
    }
  },

  // Update ticket status
  updateTicketStatus: async (ticketId: string, params: UpdateStatusParams): Promise<ReplyResponse> => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/support/admin/support-ticket/${ticketId}/status`,
        params,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error updating ticket status:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to update ticket status'
      };
    }
  },
};